import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../../lib/AuthContext";
import {
  Scales, Lock, Crown, CircleNotch, Plus, X, UploadSimple, FileText,
  ArrowLeft, Clock, Trash, Eye, DownloadSimple, CheckCircle, Warning,
  ShieldWarning, Broadcast, BookmarkSimple, CaretRight, ArrowRight,
  MinusCircle, ArrowsClockwise,
} from "@phosphor-icons/react";
import { Button } from "../../components/ui/button";
import ToolInfoFooter from "../../components/ToolInfoFooter";
import { RECONCILE_TOOL } from "../../lib/toolCopy";
import { trackEvent } from "../../lib/analytics";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TEAM_TIERS = new Set(["free", "pro", "team", "business", "enterprise"]);

export default function DashboardRoster() {
  const { user, authFetch } = useAuth();
  const tier = user?.tier || "free";
  const canReconcile = TEAM_TIERS.has(tier);

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [activeJobId, setActiveJobId] = useState(null);

  const loadJobs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${window.location.origin}/api/roster/jobs`);
      if (res.ok) { const d = await res.json(); setJobs(d.jobs || []); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [authFetch]);

  useEffect(() => { if (canReconcile) loadJobs(); else setLoading(false); }, [canReconcile, loadJobs]);

  const handleDelete = async (jobId) => {
    try {
      await authFetch(`${window.location.origin}/api/roster/jobs/${jobId}`, { method: "DELETE" });
      setJobs(prev => prev.filter(j => j.job_id !== jobId));
      if (activeJobId === jobId) setActiveJobId(null);
    } catch (e) { console.error(e); }
  };

  if (!canReconcile) {
    return (
      <>
        <Helmet><title>Roster Reconciliation | NPI Lookup</title></Helmet>
        <div data-testid="dashboard-roster">
          <h1 className="font-display font-bold text-2xl text-slate-900 mb-6">Roster Reconciliation</h1>
          <div className="bg-white rounded-xl border-2 border-slate-200 p-12 text-center">
            <Lock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h2 className="font-display font-bold text-xl text-slate-900 mb-2">Roster Reconciliation is a Team feature</h2>
            <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">
              Upload your HR roster → instantly see compliance risks, monitoring coverage gaps, and stale monitoring entries.
              Built for credentialing coordinators at MSOs, staffing firms, and credentialing agencies.
            </p>
            <Link to="/pricing">
              <Button className="bg-teal hover:bg-teal/90 text-white rounded-xl gap-2 h-11 px-6">
                <Crown className="w-4 h-4" weight="fill" /> Upgrade to Team
              </Button>
            </Link>
          </div>
        </div>
      </>
    );
  }

  if (activeJobId) {
    return <ReconcileReport jobId={activeJobId} authFetch={authFetch} onBack={() => { setActiveJobId(null); loadJobs(); }} />;
  }

  return (
    <>
      <Helmet><title>Roster Reconciliation | NPI Lookup</title></Helmet>
      <div data-testid="dashboard-roster">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display font-bold text-2xl text-slate-900">Roster Reconciliation</h1>
            <p className="text-sm text-slate-500">{RECONCILE_TOOL.tagline}</p>
          </div>
          <Button onClick={() => setShowNew(true)} className="bg-teal hover:bg-teal/90 text-white rounded-lg gap-2" data-testid="new-reconcile-btn">
            <Plus className="w-4 h-4" /> New Reconciliation
          </Button>
        </div>

        {showNew && <NewReconciliationModal authFetch={authFetch} onClose={() => setShowNew(false)} onCreated={(jid) => { setShowNew(false); setActiveJobId(jid); loadJobs(); }} />}

        {loading ? (
          <div className="p-12 text-center"><CircleNotch className="w-6 h-6 text-teal animate-spin mx-auto" /></div>
        ) : jobs.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Scales className="w-10 h-10 text-slate-300 mx-auto mb-3" weight="fill" />
            <h3 className="font-display font-semibold text-slate-900 mb-1">No reconciliations yet</h3>
            <p className="text-sm text-slate-500 mb-4">Upload a roster to find compliance risks and coverage gaps across your entire team.</p>
            <Button onClick={() => setShowNew(true)} className="bg-teal hover:bg-teal/90 text-white rounded-lg gap-2"><Plus className="w-4 h-4" /> Run First Reconciliation</Button>
          </div>
        ) : (
          <div>
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
              <Clock className="w-4 h-4" /> Recent Reconciliations
              <span className="text-xs font-normal text-slate-400 normal-case">— auto-deleted after 30 days</span>
            </h2>
            <div className="space-y-2">
              {jobs.map(job => <JobCard key={job.job_id} job={job} onView={setActiveJobId} onDelete={handleDelete} />)}
            </div>
          </div>
        )}

        <ToolInfoFooter title={RECONCILE_TOOL.title} steps={RECONCILE_TOOL.steps} faqs={RECONCILE_TOOL.faqs} />
      </div>
    </>
  );
}

function JobCard({ job, onView, onDelete }) {
  const s = job.summary || {};
  const isProcessing = job.status !== "complete";
  return (
    <div className="bg-white rounded-xl border border-slate-200 hover:border-slate-300 transition-all">
      <div className="flex items-center justify-between p-4">
        <button onClick={() => !isProcessing && onView(job.job_id)} className="flex items-center gap-3 text-left flex-1 min-w-0">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${isProcessing ? "bg-amber-50" : "bg-emerald-50"}`}>
            {isProcessing ? <CircleNotch className="w-5 h-5 text-amber-500 animate-spin" /> : <CheckCircle className="w-5 h-5 text-emerald-500" weight="fill" />}
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-medium text-slate-900 truncate">{job.name}</p>
            <div className="flex items-center gap-3 text-xs text-slate-400 mt-0.5">
              <span>{job.total} NPIs</span>
              <span>{new Date(job.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
              {!isProcessing && (
                <>
                  {s.critical_count > 0 && <span className="text-red-500 font-medium">{s.critical_count} critical</span>}
                  {s.gap_count > 0 && <span className="text-amber-600 font-medium">{s.gap_count} gap{s.gap_count !== 1 ? "s" : ""}</span>}
                  {s.stale_count > 0 && <span className="text-violet-600 font-medium">{s.stale_count} stale</span>}
                </>
              )}
              {isProcessing && <span className="text-amber-500">Processing {job.processed}/{job.total}...</span>}
            </div>
          </div>
        </button>
        <div className="flex items-center gap-0.5 shrink-0">
          {!isProcessing && (
            <>
              <button onClick={() => onView(job.job_id)} className="p-2 text-slate-400 hover:text-teal rounded-lg hover:bg-slate-50" title="View"><Eye className="w-4 h-4" /></button>
              <a href={`${API_URL}/api/roster/jobs/${job.job_id}/export`} target="_blank" rel="noreferrer" className="p-2 text-slate-400 hover:text-teal rounded-lg hover:bg-slate-50" title="Export CSV"><DownloadSimple className="w-4 h-4" /></a>
            </>
          )}
          <button onClick={() => onDelete(job.job_id)} className="p-2 text-slate-400 hover:text-red-500 rounded-lg hover:bg-red-50" title="Delete"><Trash className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}

function NewReconciliationModal({ authFetch, onClose, onCreated }) {
  const [source, setSource] = useState("paste");
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [existingJobId, setExistingJobId] = useState("");
  const [bulkJobs, setBulkJobs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(`${window.location.origin}/api/bulk-screening/history`);
        if (res.ok) { const d = await res.json(); setBulkJobs((d.jobs || []).filter(j => j.status === "complete")); }
      } catch (e) { console.error(e); }
    })();
  }, [authFetch]);

  const npiCount = text.trim() ? text.split(/[\n,\s]+/).filter(t => t.trim().replace(/\D/g, "").length === 10).length : 0;

  const handleSubmit = async () => {
    setSubmitting(true); setError("");
    try {
      const fd = new FormData();
      fd.append("source", source);
      fd.append("name", name.trim());
      if (source === "paste") fd.append("text", text);
      if (source === "upload" && file) fd.append("file", file);
      if (source === "existing_job") fd.append("existing_job_id", existingJobId);
      const res = await authFetch(`${window.location.origin}/api/roster/reconcile`, { method: "POST", body: fd });
      const d = await res.json();
      if (!res.ok) { setError(d.detail || "Failed"); setSubmitting(false); return; }
      trackEvent("reconcile_run", { source, roster_size: d.total || 0 });
      onCreated(d.job_id);
    } catch { setError("Failed to submit"); }
    finally { setSubmitting(false); }
  };

  const canSubmit = (source === "paste" && npiCount > 0) ||
                    (source === "upload" && file) ||
                    (source === "existing_job" && existingJobId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-display font-bold text-lg text-slate-900">New Reconciliation</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Roster Name</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. Q2 2026 Active Contractors"
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal/30" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Source</label>
            <div className="flex flex-col gap-1.5 bg-slate-50 rounded-lg p-1">
              {[
                ["paste", "Paste NPIs", "Copy-paste from a spreadsheet"],
                ["upload", "Upload CSV", "Upload a full roster file"],
                ["existing_job", "Use existing bulk screening", "Reuses data from a recent bulk run"],
              ].map(([v, label, desc]) => (
                <button key={v} type="button" onClick={() => setSource(v)}
                  className={`text-left px-3 py-2 rounded-lg text-sm transition-colors ${source === v ? "bg-white border border-teal/30 shadow-sm" : "border border-transparent hover:bg-white/50"}`}>
                  <p className={`font-medium ${source === v ? "text-teal" : "text-slate-700"}`}>{label}</p>
                  <p className="text-xs text-slate-500">{desc}</p>
                </button>
              ))}
            </div>
          </div>

          {source === "paste" && (
            <div>
              <textarea value={text} onChange={e => setText(e.target.value)}
                placeholder="Paste NPI numbers — one per line, comma-separated, or space-separated..."
                rows={5}
                className="w-full text-sm font-mono border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal/30 bg-slate-50" />
              {npiCount > 0 && <p className="text-xs text-teal font-medium mt-1">{npiCount} valid NPIs</p>}
            </div>
          )}

          {source === "upload" && (
            <div>
              <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={e => setFile(e.target.files?.[0] || null)} />
              {file ? (
                <div className="flex items-center gap-2 text-sm py-2"><FileText className="w-4 h-4 text-teal" weight="fill" /><span className="text-slate-700">{file.name}</span>
                  <button onClick={() => setFile(null)} className="text-red-500 text-xs ml-2">Remove</button></div>
              ) : (
                <button onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-slate-200 rounded-lg p-4 text-center text-sm text-slate-500 hover:border-teal/50">
                  <UploadSimple className="w-5 h-5 mx-auto mb-1" /> Click to select CSV
                </button>
              )}
            </div>
          )}

          {source === "existing_job" && (
            <div>
              {bulkJobs.length === 0 ? (
                <p className="text-xs text-slate-500 py-2">No completed bulk screening jobs found. Run one first, or use paste/upload.</p>
              ) : (
                <select value={existingJobId} onChange={e => setExistingJobId(e.target.value)}
                  className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white">
                  <option value="">Select a bulk screening job…</option>
                  {bulkJobs.map(j => (
                    <option key={j.job_id} value={j.job_id}>
                      {j.title || `Job ${j.job_id.slice(0, 8)}`} — {j.total} NPIs — {new Date(j.created_at).toLocaleDateString()}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50">
          <Button onClick={onClose} variant="outline" size="sm" className="rounded-lg">Cancel</Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || submitting}
            className="bg-teal hover:bg-teal/90 text-white rounded-lg gap-2">
            {submitting ? <CircleNotch className="w-4 h-4 animate-spin" /> : <Scales className="w-4 h-4" weight="fill" />}
            Run Reconciliation
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Report View ─────────────────────────────────────────────────

function ReconcileReport({ jobId, authFetch, onBack }) {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("critical");
  const [selected, setSelected] = useState(new Set());
  const [addListId, setAddListId] = useState("");
  const [monitoringLists, setMonitoringLists] = useState([]);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await authFetch(`${window.location.origin}/api/roster/jobs/${jobId}`);
      if (res.ok) {
        const d = await res.json();
        setJob(d);
        if (d.status === "complete" && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [authFetch, jobId]);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 2500);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(`${window.location.origin}/api/monitoring/lists`);
        if (res.ok) { const d = await res.json(); setMonitoringLists(d.lists || []); }
      } catch (e) { console.error(e); }
    })();
  }, [authFetch]);

  useEffect(() => { setSelected(new Set()); setActionMsg(null); }, [tab]);

  const rows = job ? {
    critical: job.critical || [],
    gaps: job.gaps || [],
    stale: job.stale || [],
    clean: job.clean_sample || [],
  }[tab] : [];

  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map(r => r.npi)));
  };
  const toggleRow = (npi) => {
    const next = new Set(selected);
    if (next.has(npi)) next.delete(npi); else next.add(npi);
    setSelected(next);
  };

  const handleAddToMonitoring = async () => {
    if (!addListId || selected.size === 0) return;
    setActionBusy(true); setActionMsg(null);
    try {
      const res = await authFetch(`${window.location.origin}/api/roster/jobs/${jobId}/add-to-monitoring`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ list_id: addListId, npis: Array.from(selected) }),
      });
      const d = await res.json();
      if (res.ok) {
        setActionMsg({ kind: "ok", text: `Added ${d.added} provider${d.added !== 1 ? "s" : ""} to monitoring${d.skipped_existing ? ` (${d.skipped_existing} already in list)` : ""}${d.skipped_limit ? ` (${d.skipped_limit} skipped — tier limit)` : ""}.` });
        setSelected(new Set());
      } else {
        setActionMsg({ kind: "err", text: d.detail || "Failed to add" });
      }
    } catch { setActionMsg({ kind: "err", text: "Request failed" }); }
    finally { setActionBusy(false); }
  };

  const handleRemoveStale = async () => {
    if (selected.size === 0) return;
    setActionBusy(true); setActionMsg(null);
    try {
      const res = await authFetch(`${window.location.origin}/api/roster/jobs/${jobId}/remove-stale`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ npis: Array.from(selected) }),
      });
      const d = await res.json();
      if (res.ok) {
        setActionMsg({ kind: "ok", text: `Removed ${d.removed} stale provider${d.removed !== 1 ? "s" : ""} from monitoring.` });
        setSelected(new Set());
        load();
      } else {
        setActionMsg({ kind: "err", text: d.detail || "Failed" });
      }
    } catch { setActionMsg({ kind: "err", text: "Request failed" }); }
    finally { setActionBusy(false); }
  };

  if (loading && !job) return <div className="p-12 text-center"><CircleNotch className="w-6 h-6 text-teal animate-spin mx-auto" /></div>;
  if (!job) return <div className="p-12 text-center text-slate-500">Job not found.</div>;

  const s = job.summary || {};
  const isProcessing = job.status !== "complete";
  const progress = job.total ? Math.round((job.processed / job.total) * 100) : 0;

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <button onClick={onBack} className="text-slate-400 hover:text-slate-600"><ArrowLeft className="w-5 h-5" /></button>
        <div className="flex-1 min-w-0">
          <h1 className="font-display font-bold text-xl text-slate-900 truncate">{job.name}</h1>
          <p className="text-xs text-slate-400">{job.total} NPIs · {new Date(job.created_at).toLocaleString()}</p>
        </div>
        {!isProcessing && (
          <a href={`${API_URL}/api/roster/jobs/${jobId}/export`} target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm" className="rounded-lg gap-1.5"><DownloadSimple className="w-4 h-4" /> Export CSV</Button>
          </a>
        )}
      </div>

      {isProcessing && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-slate-600">Screening & reconciling…</span>
            <span className="text-slate-500">{job.processed || 0} / {job.total || 0}</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-teal rounded-full transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      {!isProcessing && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 mb-4">
            <SummaryTile label="Roster" value={s.roster_total} icon={Scales} />
            <SummaryTile label="Critical" value={s.critical_count} color="text-red-600 border-red-100 bg-red-50" icon={ShieldWarning} />
            <SummaryTile label="Coverage Gaps" value={s.gap_count} color="text-amber-600 border-amber-100 bg-amber-50" icon={Warning} />
            <SummaryTile label="Stale" value={s.stale_count} color="text-violet-600 border-violet-100 bg-violet-50" icon={ArrowsClockwise} />
            <SummaryTile label="Clean" value={s.clean_count} color="text-emerald-600 border-emerald-100 bg-emerald-50" icon={CheckCircle} />
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="flex border-b border-slate-200 overflow-x-auto">
              <TabBtn active={tab === "critical"} onClick={() => setTab("critical")} label="Critical Risks" count={s.critical_count} color="red" testId="tab-critical" />
              <TabBtn active={tab === "gaps"} onClick={() => setTab("gaps")} label="Coverage Gaps" count={s.gap_count} color="amber" testId="tab-gaps" />
              <TabBtn active={tab === "stale"} onClick={() => setTab("stale")} label="Stale Monitoring" count={s.stale_count} color="violet" testId="tab-stale" />
              <TabBtn active={tab === "clean"} onClick={() => setTab("clean")} label="Clean & Covered" count={s.clean_count} color="emerald" testId="tab-clean" />
            </div>

            {/* Action bar */}
            {(tab === "gaps" || tab === "critical" || tab === "stale") && rows.length > 0 && (
              <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 flex flex-wrap items-center gap-2">
                <span className="text-xs text-slate-500">{selected.size} selected</span>
                {(tab === "gaps" || tab === "critical") && (
                  <>
                    <select value={addListId} onChange={e => setAddListId(e.target.value)}
                      className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white">
                      <option value="">Choose monitoring list…</option>
                      {monitoringLists.map(l => <option key={l.list_id} value={l.list_id}>{l.name} ({l.provider_count || 0})</option>)}
                    </select>
                    <Button size="sm" onClick={handleAddToMonitoring} disabled={!addListId || selected.size === 0 || actionBusy}
                      className="bg-teal hover:bg-teal/90 text-white rounded-lg gap-1.5 text-xs"
                      data-testid="add-to-monitoring-btn">
                      {actionBusy ? <CircleNotch className="w-3.5 h-3.5 animate-spin" /> : <Broadcast className="w-3.5 h-3.5" />}
                      Add to monitoring
                    </Button>
                  </>
                )}
                {tab === "stale" && (
                  <Button size="sm" onClick={handleRemoveStale} disabled={selected.size === 0 || actionBusy}
                    variant="outline" className="rounded-lg gap-1.5 text-xs border-red-200 text-red-600 hover:bg-red-50"
                    data-testid="remove-stale-btn">
                    {actionBusy ? <CircleNotch className="w-3.5 h-3.5 animate-spin" /> : <MinusCircle className="w-3.5 h-3.5" />}
                    Remove from monitoring
                  </Button>
                )}
                {actionMsg && (
                  <span className={`text-xs font-medium ml-auto ${actionMsg.kind === "ok" ? "text-emerald-600" : "text-red-500"}`}>
                    {actionMsg.text}
                  </span>
                )}
              </div>
            )}

            {rows.length === 0 ? (
              <div className="p-10 text-center text-sm text-slate-400">
                {tab === "critical" && <span>No critical compliance risks. 🎉</span>}
                {tab === "gaps" && <span>Every roster provider is already being monitored.</span>}
                {tab === "stale" && <span>No stale monitoring entries.</span>}
                {tab === "clean" && <span>No clean records.</span>}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      {tab !== "clean" && (
                        <th className="px-3 py-2.5 w-8">
                          <input type="checkbox" checked={rows.length > 0 && selected.size === rows.length}
                            onChange={toggleAll} className="rounded border-slate-300" />
                        </th>
                      )}
                      <th className="text-left px-3 py-2.5 font-semibold text-slate-600">NPI</th>
                      <th className="text-left px-3 py-2.5 font-semibold text-slate-600">Provider</th>
                      <th className="text-left px-3 py-2.5 font-semibold text-slate-600 hidden md:table-cell">Specialty</th>
                      <th className="text-left px-3 py-2.5 font-semibold text-slate-600 hidden lg:table-cell">State</th>
                      {tab === "critical" && <th className="text-left px-3 py-2.5 font-semibold text-slate-600">Flags</th>}
                      {tab === "gaps" && <th className="text-center px-3 py-2.5 font-semibold text-slate-600">PECOS</th>}
                      {tab === "stale" && <th className="text-left px-3 py-2.5 font-semibold text-slate-600">In List</th>}
                      {tab === "clean" && <th className="text-center px-3 py-2.5 font-semibold text-slate-600">Coverage</th>}
                      <th className="text-right px-3 py-2.5 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => (
                      <tr key={r.npi} className={`border-b border-slate-100 hover:bg-slate-50/50 ${selected.has(r.npi) ? "bg-teal/5" : ""}`}>
                        {tab !== "clean" && (
                          <td className="px-3 py-2.5">
                            <input type="checkbox" checked={selected.has(r.npi)} onChange={() => toggleRow(r.npi)}
                              className="rounded border-slate-300" data-testid={`select-${r.npi}`} />
                          </td>
                        )}
                        <td className="px-3 py-2.5 font-mono text-slate-700">{r.npi}</td>
                        <td className="px-3 py-2.5 font-medium text-slate-900 max-w-[200px] truncate">{r.provider_name || "—"}</td>
                        <td className="px-3 py-2.5 text-slate-600 max-w-[160px] truncate hidden md:table-cell">{r.specialty || "—"}</td>
                        <td className="px-3 py-2.5 text-slate-500 hidden lg:table-cell">{r.state || "—"}</td>
                        {tab === "critical" && (
                          <td className="px-3 py-2.5">
                            <div className="flex flex-wrap gap-1">
                              {(r.flags || []).map((f, i) => (
                                <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded-full">
                                  <ShieldWarning className="w-3 h-3" weight="fill" />{f}
                                </span>
                              ))}
                            </div>
                          </td>
                        )}
                        {tab === "gaps" && (
                          <td className="px-3 py-2.5 text-center">
                            {r.pecos_enrolled === true ? <span className="text-emerald-600 text-[11px] font-semibold">Yes</span>
                              : r.pecos_enrolled === false ? <span className="text-slate-400 text-[11px]">No</span>
                              : <span className="text-slate-300 text-[11px]">—</span>}
                          </td>
                        )}
                        {tab === "stale" && (
                          <td className="px-3 py-2.5 text-slate-500 text-xs">{r.list_name || "—"}</td>
                        )}
                        {tab === "clean" && (
                          <td className="px-3 py-2.5 text-center">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] font-semibold rounded-full">
                              <CheckCircle className="w-3 h-3" weight="fill" /> Covered
                            </span>
                            {r.saved && <BookmarkSimple className="w-3 h-3 text-teal inline-block ml-1" weight="fill" />}
                          </td>
                        )}
                        <td className="px-3 py-2.5 text-right">
                          <Link to={`/provider/${r.npi}`} className="text-slate-400 hover:text-teal"><Eye className="w-4 h-4" /></Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {tab === "clean" && s.clean_count > (job.clean_sample || []).length && (
              <div className="p-3 text-xs text-slate-400 text-center border-t border-slate-100">
                Showing {(job.clean_sample || []).length} of {s.clean_count} clean providers. Export CSV for the full list.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SummaryTile({ label, value, color = "text-slate-900 border-slate-200 bg-white", icon: Icon }) {
  return (
    <div className={`rounded-xl border px-3 py-2.5 ${color}`}>
      <div className="flex items-center justify-between">
        <p className="text-2xl font-bold">{value ?? 0}</p>
        {Icon && <Icon className="w-5 h-5 opacity-60" weight="fill" />}
      </div>
      <p className="text-[11px] font-medium opacity-80">{label}</p>
    </div>
  );
}

function TabBtn({ active, onClick, label, count, color, testId }) {
  const colorMap = {
    red: "text-red-600",
    amber: "text-amber-600",
    violet: "text-violet-600",
    emerald: "text-emerald-600",
  };
  return (
    <button onClick={onClick} data-testid={testId}
      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
        active ? "border-navy text-navy" : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
      }`}>
      <span>{label}</span>
      {count > 0 && <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold ${active ? colorMap[color] : "bg-slate-100 text-slate-500"} ${active ? "bg-slate-100" : ""}`}>{count}</span>}
    </button>
  );
}
